import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, actionKey } from '../../api';
import type { RelSummary } from '../../types';
import { useClaims } from '../shared/useClaims';
import { ConfirmDialog, Empty, ErrorBox, Field, Loading, day, useBusy } from '../../ui';
import { useSession } from '../../session';
import { Prices } from './Prices';
import { Icon, Ticket, clockTime, plateText, rs } from '../../brand';

interface SupplierDash {
  customers: RelSummary[];
  totals: { outstandingPaise: string; overduePaise: string; reservedPaise: string };
  today: { supplies: number; amountPaise: string; litres: string };
  pendingPayments: number;
  pendingPaymentsPaise?: string;
  openDisputes: number;
  staleClaims: number;
  activeClaims: number;
}

const OVERDUE_BUCKETS = ['OVERDUE_1_7', 'OVERDUE_8_30', 'OVERDUE_30_PLUS'];
const BILLED_BUCKETS = ['CURRENT', 'DUE_SOON', 'DUE_TODAY'];

function sumBuckets(customers: RelSummary[], buckets: string[]) {
  return customers.reduce((sum, c) => sum + buckets.reduce((s, b) => s + BigInt(c.ageing[b] ?? '0'), 0n), 0n);
}

export function SupplierDashboard() {
  const { can } = useSession();
  const q = useQuery({ queryKey: ['supplier-dash'], queryFn: () => api.get<SupplierDash>('/api/supplier/dashboard'), refetchInterval: 30_000 });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data!;
  const live = d.customers.filter((c) => c.status !== 'CLOSED');
  const owed = BigInt(d.totals.outstandingPaise);
  const overdue = sumBuckets(live, OVERDUE_BUCKETS);
  const billed = sumBuckets(live, BILLED_BUCKETS);
  const notBilled = sumBuckets(live, ['NOT_BILLED']);
  const pct = (v: bigint) => (owed > 0n ? Number((v * 1000n) / owed) / 10 : 0);
  return (
    <div className="stack">
      <h1 className="sr-only">Pump home</h1>
      <section className="credit-hero" aria-label="Money customers owe you">
        <div className="top">
          <span>Customers owe you</span>
          {overdue > 0n ? <span className="hero-pill bad">{rs(overdue.toString())} overdue</span> : <span className="hero-pill">Nothing overdue</span>}
        </div>
        <div className="big">{rs(d.totals.outstandingPaise)}</div>
        <div className="credit-bar" aria-hidden="true">
          <span className="overdue" style={{ width: `${pct(overdue)}%` }} />
          <span className="owed" style={{ width: `${pct(billed)}%` }} />
          <span className="held" style={{ width: `${pct(notBilled)}%` }} />
        </div>
        <div className="credit-legend">
          <span><i style={{ background: '#f28b82' }} />Overdue {rs(overdue.toString())}</span>
          <span><i style={{ background: '#f2b33d' }} />On bills {rs(billed.toString())}</span>
          <span><i style={{ background: '#8fd3ad' }} />Not billed yet {rs(notBilled.toString())}</span>
        </div>
      </section>
      {can('supplier.prices.manage') ? <Prices /> : null}
      <div className="tiles">
        {d.pendingPayments ? (
          <Link className="tile amber" to="/supplier/payments">
            <span className="t-label">Payments to check</span>
            <span className="t-value">{rs(d.pendingPaymentsPaise ?? '0')}</span>
            <span className="t-sub">{d.pendingPayments} payment{d.pendingPayments > 1 ? 's' : ''}</span>
            <span className="t-link">Check and confirm →</span>
          </Link>
        ) : (
          <Link className="tile" to="/supplier/payments">
            <span className="t-label">Payments to check</span>
            <span className="t-value">{rs('0')}</span>
            <span className="t-sub">Nothing waiting</span>
          </Link>
        )}
        <div className="tile">
          <span className="t-label">Sold on credit today</span>
          <span className="t-value">{rs(d.today.amountPaise)}</span>
          <span className="t-sub">{d.today.supplies} fill{d.today.supplies === 1 ? '' : 's'} · {Math.round(Number(d.today.litres))} L</span>
          <span className="t-sub muted">{rs(d.totals.reservedPaise)} approved, not filled yet</span>
        </div>
      </div>
      {d.activeClaims ? (
        <Link className="strip info" to="/supplier/claims">
          <Icon name="clock" size={20} />
          <span>{d.activeClaims} fuel {d.activeClaims === 1 ? 'entry needs' : 'entries need'} finishing{d.staleClaims ? ` · ${d.staleClaims} waiting over 45 minutes` : ''}</span>
          <Icon name="next" size={18} stroke={2.4} />
        </Link>
      ) : null}
      {d.openDisputes ? (
        <Link className="strip warn" to="/disputes">
          <Icon name="alert" size={20} />
          <span>{d.openDisputes} open dispute{d.openDisputes > 1 ? 's' : ''}</span>
          <Icon name="next" size={18} stroke={2.4} />
        </Link>
      ) : null}
      <div className="section-head">
        <h2>Customers</h2>
        <Link to="/supplier/customers">See all</Link>
      </div>
      {!live.length ? <Empty>No customers yet. Invite one from Customers.</Empty> : <CustomerRows customers={live} />}
    </div>
  );
}

/** One row per customer: what they owe, how much of the limit is used, and when the bill is due. */
export function CustomerRows({ customers, hrefBase = '/supplier/customers', side = 'supplier' }: { customers: RelSummary[]; hrefBase?: string; side?: 'buyer' | 'supplier' }) {
  return (
    <ul className="rows">
      {customers.map((c) => {
        const limit = c.terms ? BigInt(c.terms.creditLimitPaise) : 0n;
        const owes = BigInt(c.position.outstandingPaise);
        const used = limit > 0n ? Math.min(100, Number((owes * 100n) / limit)) : 0;
        const late = BigInt(c.overduePaise) > 0n;
        const stopped = c.freeze.frozen || c.stoppedForOverdue;
        const tone = late || stopped ? 'bad' : used >= 80 ? 'warn' : 'ok';
        return (
          <li key={c.relationshipId}>
            <div className="row between" style={{ flexWrap: 'nowrap' }}>
              <Link className="row-title" to={`${hrefBase}/${c.relationshipId}`}>{c.counterpartyName}</Link>
              <span className={`num row-amount ${late ? 'bad' : ''}`}>{rs(c.position.outstandingPaise)}</span>
            </div>
            <div className="meter" aria-hidden="true"><span className={`meter-${tone}`} style={{ width: `${used}%` }} /></div>
            <div className="row between row-sub">
              {late ? <span className="bad-text">Overdue {c.oldestOverdueDays} day{c.oldestOverdueDays === 1 ? '' : 's'}{stopped ? ' · credit stopped' : ''}</span>
                : stopped ? <span className="bad-text">Credit stopped</span>
                : c.status !== 'ACTIVE' ? <span>{c.status === 'CLOSED' ? 'Archived' : c.status.replace(/_/g, ' ').toLowerCase()}</span>
                : <span>{limit > 0n ? `${used}% of ${rs(c.terms!.creditLimitPaise)} limit${side === 'buyer' ? ' used' : ''}` : 'No credit terms yet'}</span>}
              <span>{c.nextDueDate && BigInt(c.nextDueAmountPaise) > 0n ? `Bill due ${day(c.nextDueDate)}` : ''}</span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

interface OpenClaim {
  indentId: string;
  number: string;
  personName: string;
  vehicleRegistration?: string;
  fuelType?: string;
  limit: string;
  claimedAt: string;
  claimedBy: string;
  stale: boolean;
}

export function Claims() {
  const qc = useQueryClient();
  const q = useClaims<OpenClaim>('claims', 20_000);
  const [sel, setSel] = useState<OpenClaim | null>(null);
  const [mode, setMode] = useState<'none' | 'supply'>('none');
  const [note, setNote] = useState('');
  const [qty, setQty] = useState('');
  const { busy, error, run, setError } = useBusy();
  const close = () => { setSel(null); setNote(''); setQty(''); setError(null); };
  return (
    <div className="stack">
      <h1>Finish fuel entries</h1>
      <p className="muted">A staff member tapped “Start filling” for these indents, but has not saved the fuel details yet. Check with the attendant before finishing an entry.</p>
      <div className="card">
        <p><strong>Fuel was given:</strong> choose “Enter fuel given” and enter the actual litres from the pump or receipt.</p>
        <p><strong>No fuel was given:</strong> choose “No fuel given” to cancel the indent without charging the customer.</p>
        <p className="muted">Still filling? Leave the entry here until filling is finished.</p>
      </div>
      {q.isLoading ? <Loading /> : <ErrorBox error={q.error} />}
      {!q.isLoading && !q.error && !q.items.length ? <Empty>No fuel entries to finish. Entries will appear here when staff start filling.</Empty> : null}
      <ul className="tickets">
        {q.items.map((c) => (
          <Ticket
            key={c.indentId}
            t={{ fuelType: c.fuelType, plate: c.vehicleRegistration, driver: c.personName || undefined, limit: c.limit, status: 'CLAIMED', statusLabel: 'Details not saved', extra: <span className="ticket-extra">Indent {c.number} · Started {clockTime(c.claimedAt)} by {c.claimedBy}{c.stale ? ' · Not saved for over 45 minutes' : ''}</span> }}
            actions={<>
              <button className="btn primary" onClick={() => { setSel(c); setMode('supply'); }}>Enter fuel given</button>
              <button className="btn" onClick={() => { setSel(c); setMode('none'); }}>No fuel given</button>
            </>}
          />
        ))}
      </ul>
      {q.hasNextPage ? <button className="btn" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>Show older entries</button> : null}
      {sel ? (
        <ConfirmDialog open title={mode === 'none' ? 'Cancel this indent without charging?' : 'Enter fuel given'} lines={[['Indent', sel.number], ['Driver', sel.personName || 'Not provided'], ['Vehicle', sel.vehicleRegistration ? plateText(sel.vehicleRegistration) : 'Not provided'], ['Started by', sel.claimedBy]]} confirmLabel={mode === 'none' ? 'Confirm no fuel given' : 'Save fuel entry'} danger={mode === 'none'} busy={busy} error={error} onClose={close} onConfirm={() => void run(async () => {
          if (mode === 'none') {
            const k = actionKey(`release:${sel.indentId}`);
            await api.post(`/api/redeem/${sel.indentId}/release`, { note }, { idempotencyKey: k.key });
            k.done();
          } else {
            const k = actionKey(`supply:${sel.indentId}`);
            await api.post(`/api/redeem/${sel.indentId}/supply`, { quantity: qty, confirmSupplied: true, evidenceIds: [] }, { idempotencyKey: k.key });
            k.done();
          }
          close();
          await qc.invalidateQueries();
        })}>
          {mode === 'none' ? <>
            <p>Confirm this only after checking that no fuel was given. The customer will not be charged. This indent and its code can no longer be used.</p>
            <Field label="Why was no fuel given?" hint="For example: the driver left before filling. Say who you checked with."><input value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          </> : <>
            <p>Enter the actual litres from the pump display or receipt. The amount will be calculated using the fuel price saved when filling started and added to the customer’s account.</p>
            <Field label="Litres of fuel given" hint="For example: 25.500. Check the number before saving."><input inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
          </>}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}
